import React, { PureComponent, ReactElement } from 'react';

import { shouldRenderMobile } from '@philips-emr/hpms-stipulator-commons';
import { Fetch, Form, Repeater, Text } from '@philips-emr/tws-components';
import { AwesomeComponent, ComponentType, ContainerProvider, Oswald, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, intellisense, IntellisenseExpectedType, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { FetchEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, enumValues, isEmptyArrayOrObject, isEmptyOrBlankString, isFunction } from '@philips-emr/tws-utils';

import HpmsCustomContentCollapseApi from '../../api/hpms-custom-content-collapse-api';
import { ActionRenderType, HpmsCustomContentCollapseEvent, LayoutType } from '../../types/menu-collapse-card-type';

import HpmsCustomCard, { HpmsCustomCardProps } from './hpms-custom-card';

import './hpms-custom-card-collapse.css';

interface PropsExpandedContent {
  children: ReactElement | ReactElement[];
  endpoint: string;
  data: Record<string, Value>;
  uuid: string;
  hideActionCollapseIfEmptyResult: boolean;
  onOpen?: (event: HpmsCustomContentCollapseEvent<Record<string, Value>>) => void;
  onAfterFetch?: (event: FetchEvent) => void;
  title: string;
  type: LayoutType;
  lazy: boolean;
  isList: boolean;
  showDivider: boolean;
}

interface StateExpandedContent {
  endpoint: string;
  hideActionCollapseIfEmptyResult: boolean;
  showData: boolean;
  showTitle: boolean;
}

@AwesomeComponent({ api: HpmsCustomContentCollapseApi, type: ComponentType.DATA_DISPLAY })
export default class HpmsCustomCardContentCollapse extends PureComponent<PropsExpandedContent, StateExpandedContent> {

  public static propTypes = {
    endpoint: intellisense(string, IntellisenseExpectedType.URL).group('fetch'),
    lazy: bool.group('fetch'),
    hideActionCollapseIfEmptyResult: bool.group('fetch'),
    showDivider: bool,
    onAfterFetch: eventListener,
    onOpen: eventListener,
    isList: bool,
    data: object.notEnumerable(),
    parameters: object.notEnumerable(),
    uuid: string,
    title: string,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    type: oneOf(enumValues(LayoutType)),
  } as any;

  static defaultProps = {
    isList: true,
    endpoint: '',
    type: LayoutType.SIMPLE,
    showDivider: false,
    hideActionCollapseIfEmptyResult: false,
  };

  public state = {
    endpoint: '',
    showData: true,
    showTitle: false,
    visible: true,
    hideActionCollapseIfEmptyResult: false,
    data: '',
  };

  public render(): ReactElement | null {
    if (Oswald.isDesigning()) {
      return this.getCard();
    }

    const { showData } = this.state;

    if (!showData) {
      return null;
    }

    const { visible } = this.state;
    if (!visible) {
      return null;
    }

    const { onOpen, data } = this.props;

    if (!data.isCollapsed) {
      return null;
    }

    let { endpoint } = this.state;
    let parameters = {};
    let props;

    if (isFunction(onOpen)) {
      const action: ActionRenderType = (param: { endpoint?: string, parameters?: Record<string, Value> }) => {
        if (param.endpoint && !isEmptyOrBlankString(param.endpoint)) {
          endpoint = param.endpoint;
        }

        if (param.parameters) {
          parameters = param.parameters;
        }
      };

      const event = new HpmsCustomContentCollapseEvent({
        onOpenRender: action,
        data,
      });

      onOpen(event);
    }

    if (!endpoint) {
      return null;
    }

    if (isEmptyOrBlankString(endpoint)) {
      props = { ...this.props };
    } else {
      props = { ...this.props, endpoint };
    }

    if (parameters) {
      props = { ...props, parameters };
    }

    const { type } = this.props;

    if (type === LayoutType.SIMPLE) {
      return this.geSimpleLayout(props);
    }

    return this.getDefaultLayout(props);
  }

  private geSimpleLayout(props: Record<string, Value>): ReactElement | null {
    const { uuid, children, showDivider, lazy, isList } = this.props;
    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }
    const { visible } = this.state;
    if (!visible) {
      return null;
    }

    const classMobile = shouldRenderMobile() ? 'c-hpms-custom-card-content-collapse__content-wrapper-mobile' : '';

    return (
      <div className="c-hpms-custom-card-content-collapse__simple-collapse">
        <Fetch {...props} uuid={uuid}
          onAfterFetch={(e: FetchEvent) => {
            this.afterFetch(e);
          }}
          lazy={lazy}>
          {showDivider && <hr className="c-hpms-custom-card-content-collapse__divider" />}
          {this.getTitle()}
          {isList ?
            <Repeater uuid={uuid}>
              <Form
                uuid={uuid}
                locked={true}
                editing={false}
                showHeader={false}
                className={'c-hpms-custom-card__content-wrapper ' + classMobile}
              >
                {children}
              </Form>
            </Repeater>
            :
            <Form
              uuid={uuid}
              locked={true}
              editing={false}
              showHeader={false}
              className={'c-hpms-custom-card__content-wrapper ' + classMobile}
            >
              {children}
            </Form>
          }
        </Fetch>
      </div>
    );
  }

  private getDefaultLayout(props: Record<string, Value>): ReactElement | null {
    const { uuid, lazy, isList } = this.props;
    const { visible } = this.state;
    if (!visible) {
      return null;
    }
    return (
      <>
        {this.getTitle()}

        <Fetch {...props} uuid={uuid} onAfterFetch={(e: any) => this.afterFetch(e)} lazy={lazy}>
          {isList ?
            <Repeater uuid={uuid}>
              {this.getCard() || <></>}
            </Repeater>
            :
            this.getCard()
          }
        </Fetch>
      </>
    );
  }

  private afterFetch(event: FetchEvent): void {
    const response = event?.data || {};
    const { onAfterFetch } = this.props;
    if (isEmptyArrayOrObject(response)) {
      const { data } = this.props;
      const onAfterFetchtHideAction = data?.onAfterFetchtHideAction;
      if (onAfterFetchtHideAction && isFunction(onAfterFetchtHideAction)) {
        onAfterFetchtHideAction();
      }

      this.setState({ showData: false });
    } else {
      this.setState({ showTitle: true });
    }
    if (onAfterFetch && isFunction(onAfterFetch)) onAfterFetch(event);
  }

  private getTitle(): ReactElement | null {
    const { uuid, title, type, hideActionCollapseIfEmptyResult } = this.props;
    const { showTitle } = this.state;

    if (isEmptyOrBlankString(title) || !showTitle) {
      return null;
    }

    const propsCard = this.getPropertyCard();

    if (type === LayoutType.SIMPLE) {
      return (
        <Text bold
          value={title}
          className="c-hpms-custom-card-content-collapse__simple-collapse--title"
        >
        </Text>
      );
    }

    return (
      <HpmsCustomCard
        key={uuid}
        {...propsCard}
        hideActionCollapseIfEmptyResult={hideActionCollapseIfEmptyResult}
        className="c-hpms-custom-card-content-collapse__content-wrapper c-hpms-custom-card-content-collapse__title"
      >
        <Text value={title} bold={true}></Text>
      </HpmsCustomCard>
    );

  }

  private getPropertyCard(): HpmsCustomCardProps {
    const { uuid } = this.props;

    return {
      uuid: uuid,
      width: 12,
      hasCollapse: false,
      showMenu: false,
      fieldsAreaWidth: '100%',
    } as unknown as HpmsCustomCardProps;
  }

  private getCard(): ReactElement | null {
    const { uuid, children, hideActionCollapseIfEmptyResult } = this.props;
    const propsCard = this.getPropertyCard();
    const { visible } = this.state;
    if (!visible) {
      return null;
    }
    return (
      <HpmsCustomCard
        key={uuid}
        {...propsCard}
        visible={this.state.visible}
        hideActionCollapseIfEmptyResult={hideActionCollapseIfEmptyResult}
        className={this.getClassNameDefaultCard()}
      >
        {children}
      </HpmsCustomCard>
    );
  }

  public componentDidMount(): void {
    this.setState({ endpoint: this.props.endpoint });
  }

  private getClassNameDefaultCard(): string {
    return classNames({
      ['c-hpms-custom-card-content-collapse__content-wrapper--last-child-border']: true,
      ['c-hpms-custom-card-content-collapse__content-wrapper']: true,
    });
  }

}
